/**
 * V1.3 W5 / HUX-06/07 / HUX-12 / R2 — Picture3+SOT nested discovery control.
 * Winning composition: [ Seznam ─ Provozovny|Tenanti ] [ Mapa ] [ ☆ ] [ Taška★ ]
 * Picture2 peer-Tenanti / flat strip = NON-AUTHORITY (do not restore).
 * Favorites = FavoriteStarIcon (not text ☆); Taška★ = BagStarIcon;
 * Mapa = MapPinIcon (G6 — not text-only).
 * Active chrome uses platform `--color-accent` (HUX-12) — not neutral primary.
 * Control 48–52px; glyphs 20–24; hits ≥44.
 */

import { useTranslation } from 'react-i18next';
import type {
  DiscoveryListKind,
  DiscoveryView,
} from '../discovery/discoverySearchParams.js';
import { BagStarIcon } from '../../shared/ui/BagStarIcon.js';
import { FavoriteStarIcon } from '../../shared/ui/FavoriteStarIcon.js';
import { MapPinIcon } from '../../shared/ui/MapPinIcon.js';
import { BrowseSeznamListKindSwitch } from './BrowseSeznamListKindSwitch.js';

export type BrowseDiscoveryModeStripProps = {
  readonly view: DiscoveryView;
  readonly onViewChange: (view: DiscoveryView) => void;
  readonly listKind: DiscoveryListKind;
  readonly onListKindChange: (listKind: DiscoveryListKind) => void;
  readonly className?: string;
};

/**
 * Visual control ≥44 hit (min-h-11 = 44px).
 * D10 / G11 / G16 — narrow 320/375: 44px peers + icon-only + tighter Seznam label so nest fits without H-overflow / overlap.
 */
const CONTROL_SIZE = 'min-h-11 h-11 min-w-11';

const PEER_BASE = [
  'inline-flex shrink-0 items-center justify-center rounded-md px-1',
  CONTROL_SIZE,
  'text-sm font-medium',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]',
].join(' ');
function peerClass(active: boolean): string {
  return [
    PEER_BASE,
    active
      ? // HUX-12 — platform accent fill (TENANT_ACCENT / --color-accent), not --color-primary.
        'bg-[var(--color-accent)] text-[var(--color-accent-foreground)] shadow-sm'
      : 'bg-[var(--color-surface-elevated)] text-[var(--color-on-surface-muted)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-on-surface)]',
  ].join(' ');
}

/** Glyph size 20px (in 20–24 band). */
const GLYPH_CLASS = 'h-5 w-5';

export function BrowseDiscoveryModeStrip({
  view,
  onViewChange,
  listKind,
  onListKindChange,
  className,
}: BrowseDiscoveryModeStripProps): JSX.Element {
  const { t } = useTranslation('customer');
  const listActive = view === 'list';
  const mapLabel = t('browse.discovery.mode.map', { defaultValue: 'Mapa' });
  const listLabel = t('browse.discovery.mode.list', { defaultValue: 'Seznam' });
  const favoritesAria = t('browse.discovery.modeFavoritesAria', {
    defaultValue: 'Oblíbené provozovny',
  });
  const bagAria = t('browse.discovery.modeBagAria', {
    defaultValue: 'Taška★ nabídky',
  });
  const bagLabel = t('browse.discovery.mode.bag', { defaultValue: 'Taška★' });

  return (
    <div
      className={[
        'flex min-w-0 w-full items-stretch gap-px rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-0.5',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role="tablist"
      aria-label={t('browse.discovery.modeStripAria', {
        defaultValue: 'Režim prohlížení',
      })}
      data-testid="customer-browse-mode-strip"
      data-discovery-nesting="picture3"
      data-d10-density="compact-44"
    >
      {/* Picture3: Seznam compound nests Provozovny|Tenanti (D02) — not peer Tenanti. */}
      <div
        className={[
          'flex min-w-0 flex-1 items-stretch gap-px rounded-md p-px',
          listActive
            ? 'bg-[var(--color-accent)] text-[var(--color-accent-foreground)] shadow-sm'
            : 'bg-[var(--color-surface-elevated)] text-[var(--color-on-surface-muted)]',
        ].join(' ')}
        data-testid="customer-browse-mode-list-cluster"
        data-hux12-accent={listActive ? '1' : '0'}
      >
        <button
          type="button"
          role="tab"
          className={[
            'inline-flex max-w-[4.5rem] shrink-0 items-center justify-center truncate rounded-md px-1 text-xs font-medium sm:max-w-none sm:px-2 sm:text-sm',
            CONTROL_SIZE,
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]',
            listActive
              ? 'text-[var(--color-accent-foreground)]'
              : 'hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-on-surface)]',
          ].join(' ')}
          aria-selected={listActive}
          aria-label={t('browse.discovery.listAria')}
          data-testid="customer-browse-mode-list"
          onClick={() => {
            onViewChange('list');
          }}
        >
          {listLabel}
        </button>
        {listActive ? (
          <BrowseSeznamListKindSwitch
            listKind={listKind}
            onListKindChange={onListKindChange}
            variant="nested"
            className="min-w-0 flex-1"
          />
        ) : null}
      </div>

      <button
        type="button"
        role="tab"
        className={peerClass(view === 'map')}
        aria-selected={view === 'map'}
        aria-label={t('browse.discovery.mapAria')}
        data-testid="customer-browse-mode-map"
        onClick={() => {
          onViewChange('map');
        }}
      >
        <span
          className="inline-flex shrink-0"
          data-testid="customer-browse-mode-map-glyph"
        >
          <MapPinIcon className={GLYPH_CLASS} />
        </span>
        <span className="sr-only">{mapLabel}</span>
      </button>

      <button
        type="button"
        role="tab"
        className={peerClass(view === 'favorites')}
        aria-selected={view === 'favorites'}
        aria-label={favoritesAria}
        data-testid="customer-browse-mode-favorites"
        onClick={() => {
          onViewChange('favorites');
        }}
      >
        <span
          className="inline-flex shrink-0"
          data-testid="customer-browse-mode-favorites-glyph"
        >
          <FavoriteStarIcon
            filled={view === 'favorites'}
            className={GLYPH_CLASS}
          />
        </span>
        <span className="sr-only">
          {t('browse.discovery.mode.favorites', { defaultValue: 'Oblíbené' })}
        </span>
      </button>

      <button
        type="button"
        role="tab"
        className={peerClass(view === 'bag')}
        aria-selected={view === 'bag'}
        aria-label={bagAria}
        data-testid="customer-browse-mode-bag"
        onClick={() => {
          onViewChange('bag');
        }}
      >
        <span
          className="inline-flex shrink-0"
          data-testid="customer-browse-mode-bag-glyph"
        >
          <BagStarIcon filled={false} className={GLYPH_CLASS} />
        </span>
        <span className="sr-only">{bagLabel}</span>
      </button>
    </div>
  );
}
